import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { Pagination } from "@/components/ui/pagination";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { MOCK_AUDIT_EVENTS } from "@/data/mock";
import { usePermission } from "@/hooks/use-permission";
import type { AuditEventResult } from "@/types";

/* USR-06 — Admin Audit Trail.
   Canonical source: Figma IAM-09 / prototype/pages/admin/audit-log.html /
   identity-access-wireframes.html#IAM-09. `MOCK_AUDIT_EVENTS` (already
   purpose-built for this batch — id/timestamp/actor/action/target/ip/
   result, matching the AuditEvent type exactly) is canonical. The
   prototype's own static table shows 10 different illustrative rows and a
   "Showing 12 of 847 entries" footer implying a much larger real dataset;
   this implementation shows the real 10-row fixture and an honest
   "Showing X of 10" count instead of fabricating a larger backend dataset.
   "Export CSV" is rendered with no onClick — the prototype's own button
   carries no handler either, and every prior "Export CSV" control in this
   codebase (Treasury Lending, STK-03 Rewards) was found genuinely inert in
   its own source and matched that way; consistent here. Target/Actor cells
   linkify any real WLT-/TXI-/USR-/SVC- id they contain to the actual
   existing route for that record — a direct, non-invented reuse of ids
   that already exist elsewhere in this app (SVC- ids link to the generic,
   real `/admin/service-accounts` list, since no per-account detail route
   exists in scope). Plain names/emails with no resolvable id are left as
   text — no relationship was invented merely to make a cell clickable. */

const RESULT_TONE: Record<AuditEventResult, "success" | "warning" | "danger" | "info"> = {
  Success: "success",
  Warning: "warning",
  Danger: "danger",
  Recovered: "info",
  "Service Down": "danger",
};

const ID_PATTERN = /\b(USR|TXI|WLT|SVC)-\d+\b/g;

function LinkifiedText({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(ID_PATTERN);
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const id = match[0];
    const prefix = match[1];
    const href = prefix === "USR" ? `/admin/users/${id}` : prefix === "TXI" ? `/transfers/${id}` : prefix === "WLT" ? `/wallets/${id}` : "/admin/service-accounts";
    parts.push(
      <Link key={`${id}-${key++}`} to={href} className="font-semibold text-primary hover:underline">
        {id}
      </Link>
    );
    lastIndex = match.index + id.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

export function AdminAuditTrail() {
  const canExport = usePermission("evidence:export");
  const [query, setQuery] = useState("");
  const [action, setAction] = useState("all");
  const [actor, setActor] = useState("all");
  const [timeRange, setTimeRange] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;

  const actions = useMemo(() => Array.from(new Set(MOCK_AUDIT_EVENTS.map((e) => e.action))).sort(), []);
  const actors = useMemo(() => Array.from(new Set(MOCK_AUDIT_EVENTS.map((e) => e.actor))).sort(), []);

  const latestTs = useMemo(
    () => Math.max(...MOCK_AUDIT_EVENTS.map((e) => new Date(e.timestamp.replace(" UTC", "Z").replace(" ", "T")).getTime())),
    []
  );

  const rows = useMemo(
    () =>
      MOCK_AUDIT_EVENTS.filter((e) => {
        const q = query.toLowerCase();
        const matchesQuery = !q || `${e.actor} ${e.action} ${e.target}`.toLowerCase().includes(q);
        const matchesAction = action === "all" || e.action === action;
        const matchesActor = actor === "all" || e.actor === actor;
        const ts = new Date(e.timestamp.replace(" UTC", "Z").replace(" ", "T")).getTime();
        const matchesTime = timeRange === "all" || latestTs - ts <= 24 * 60 * 60 * 1000;
        return matchesQuery && matchesAction && matchesActor && matchesTime;
      }),
    [query, action, actor, timeRange, latestTs]
  );

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div>
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <Link to="/admin/users" className="text-primary no-underline hover:underline">Identity</Link>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">Audit Trail</span>
      </nav>

      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">Admin Audit Trail</h1>
        <p className="mt-1 text-sm text-muted">Every administrative action across Identity &amp; Access, service accounts, and organization settings.</p>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative w-[280px] max-w-full">
          <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search actions, actors, targets..."
            className="h-[36px] w-full rounded-[8px] border border-line bg-surface pl-9 pr-3 text-[13px] text-ink outline-none transition-colors placeholder:text-subtle focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
          />
        </div>
        <Select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} aria-label="Filter by action" className="rounded-[8px] py-[9px] pl-3 pr-7 text-[12px] text-muted">
          <option value="all">All Actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </Select>
        <Select value={actor} onChange={(e) => { setActor(e.target.value); setPage(1); }} aria-label="Filter by actor" className="rounded-[8px] py-[9px] pl-3 pr-7 text-[12px] text-muted">
          <option value="all">All Actors</option>
          {actors.map((a) => <option key={a} value={a}>{a}</option>)}
        </Select>
        <Select value={timeRange} onChange={(e) => { setTimeRange(e.target.value); setPage(1); }} aria-label="Filter by time range" className="rounded-[8px] py-[9px] pl-3 pr-7 text-[12px] text-muted">
          <option value="all">All Time</option>
          <option value="24h">Last 24h</option>
        </Select>
        <Button variant="secondary" className="ml-auto rounded-[8px]" disabled={!canExport}>Export CSV</Button>
      </div>

      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg">
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Timestamp</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Actor</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Action</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Target</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">IP</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Result</th>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((e) => (
              <tr key={e.id} className="border-b border-line last:border-b-0">
                <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink">{e.timestamp}</td>
                <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink"><LinkifiedText text={e.actor} /></td>
                <td className="px-4 py-[9px] whitespace-nowrap font-mono text-[11px] text-ink">{e.action}</td>
                <td className="px-4 py-[9px] text-[11.5px] text-ink"><LinkifiedText text={e.target} /></td>
                <td className="px-4 py-[9px] whitespace-nowrap font-mono text-[11px] text-muted">{e.ip}</td>
                <td className="px-4 py-[9px]"><Badge variant={RESULT_TONE[e.result]} size="sm">{e.result}</Badge></td>
              </tr>
            ))}
          </tbody>
        </Table>
        {rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted">No audit events match your filters.</div>
        ) : (
          <Pagination page={currentPage} pageCount={pageCount} onPageChange={setPage} totalItems={rows.length} pageSize={PAGE_SIZE} />
        )}
      </TableWrapper>
    </div>
  );
}
